import React, {useCallback} from 'react';
import styled from 'styled-components';
import {FloatingButton} from 'shared/components/Header/RoomPlanner/FloatingButton';
import {useShareRoomPlannerLinkQuery} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {getLayoutId} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {useParams} from 'react-router-dom';
import {useAppSelector} from 'store/customer';
import {useDialogContext} from 'contexts';
import CopyLinkRow from 'shared/components/Header/CopyLinkRow';
import QRCode from 'react-qr-code';

const QR_SIZE = 440;

interface ShareButtonProps {
    title?: string;
}

const ShareButton = ({title = 'Share'}: ShareButtonProps) => {
    const {roomId} = useParams();
    const {showDialog} = useDialogContext();
    const layoutId = useAppSelector(getLayoutId);

    const {data, isLoading} = useShareRoomPlannerLinkQuery(
        {id: Number(roomId)},
        {skip: !roomId || !layoutId}
    );

    const showQR = useCallback(() => {
        const qrUrl = data?.qrUrl;

        if (!qrUrl) return;

        showDialog?.({
            title: 'Share',
            className: 'share-qr',
            message: (
                <>
                    <QRContainer>
                        <QRCode value={qrUrl} size={QR_SIZE} />
                    </QRContainer>
                    <CopyLinkRow url={qrUrl} />
                </>
            ),
            hideYesButton: true,
            hideNoButton: true,
            hideFooter: true,
            closeButton: true,
        });
    }, [data, showDialog]);

    if (isLoading || !data?.qrUrl) {
        return null;
    }

    return (
        <Container>
            <FloatingButton
                icon="Share.svg"
                title={title}
                onClick={showQR}
                color="secondary"
            />
        </Container>
    );
};

export default ShareButton;

const Container = styled.div`
    position: relative;
`;

// react-qr-code renders fixed width/height attributes, which overflow the
// modal on phones - cap it to the body width and let it scale down.
const QRContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 13px;

    > svg {
        width: 100%;
        height: auto;
        max-width: ${QR_SIZE}px;
    }
`;
